import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { faRegularMemoCirclePlus, faRegularTagCirclePlus } from '@awesome.me/kit-2cbcf7d4a0/icons/kit/custom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getter } from '@progress/kendo-data-query';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import cn from 'clsx';
import moment from 'moment/moment';
import style from './style.module.scss';
import ApiGrid, { ApiGridHandle } from '@components-clean/core/api-grid';
import { DEFAULT_GRID_TABLE } from '@components-clean/core/api-grid/api-grid.helper';
import CoreSplitter from '@components-clean/core/core-splitter';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { IsPopOut } from '@components-clean/core/page-toolbar/page-toolbar';
import { FilterUIConfigType } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { StudyDataFilterContextProvider, useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import { CM2Dataset } from '@interfaces/central-monitoring';
import ActionBar from '@pages-clean/central-monitoring/components/action-bar/action-bar';
import { IDataset } from '@pages-clean/central-monitoring/data-view/types';
import { IFormData, StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import AddEditSubjectTagsDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/add-edit-subject-tags-dialog';
import RemoveFromReviewBulkDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/remove-from-review-bulk-dialog';
import RemoveFromReviewDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/remove-from-review-dialog';
import SubjectReviewDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/subject-review-dialog';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import { setSessionStorageSubjectCode } from '@contexts/local-state-context';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useLocalState } from '@hooks/index';
import Constants, { SplitterOrientation } from '@utils/constants';
import { ShowError, ShowMessage } from '@utils/notification';
import { LoggedInPageProps } from '@config/routes/interfaces';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

interface IContentProps {
    studyRef: string;
    dataset: IDataset;
}

interface IDataItem {
    code: string;
}

type SelectionState = {
    selectedItemsCount: number;
    selectedField: string;
    bulkUpdateRows: SubjectHubGridRow[];
};

type SubjectHubGridRow = {
    id: number;

    code: string;
};

const Content = ({ studyRef, dataset }: IContentProps) => {
    const { t: tCommon } = useTranslation(['common']);
    const { t } = useTranslation('sm', { keyPrefix: 'subjectHub' });
    const { state, dispatch: localStateDispatch } = useLocalState();
    const study = state?.selectedStudy;
    const { sessionId } = useStudyDataFilterContext();
    const { dispatch: pageToolbarDispatch } = usePageToolbar();
    const navigate = useNavigate();
    const isPopoutPage = IsPopOut();
    const [comparisonDataset, setComparisonDataset] = useState<CM2Dataset>();
    const [formData, setFormData] = useState<IFormData>({
        studyRef,
        dataset: {
            id: dataset?.id as number,
            date: new Date(dataset?.dataCutDate as Date | string | number),
        },
        hidePanelSetup: true,
        defaultDatasetFilters: {
            dataSetFilter: null,
            dataSetsCount: null,
            comparisonDataset: null,
        },
        datasetFilters: {
            dataSetFilter: null,
            dataSetsCount: null,
            comparisonDataset,
        },
    });
    const [selectionState, setSelectionState] = useState<SelectionState>({
        selectedItemsCount: 0,
        selectedField: '',
        bulkUpdateRows: [],
    });

    const [onReviewGridHasRecords, setOnReviewGridHasRecords] = useState(false);
    const [notOnReviewGridHasRecords, setNotOnReviewGridHasRecords] = useState(false);

    const onSelectionChange = (
        event: GridSelectionChangeEvent,
        selectedState: { [id: string]: boolean | number[] },
    ) => {
        let selectedItem: SubjectHubGridRow;
        let selectedRowCount = 0;
        let selectedItemsProps: SubjectHubGridRow[] = [];

        if (Object.keys(selectedState).length !== 0) {
            const idGetter = getter('id') ?? '';

            const selectedRows = event.dataItems.filter((x) => selectedState[idGetter(x)] === true);
            selectedRowCount = selectedRows.length;
            selectedItemsProps = selectedRows.map((item: SubjectHubGridRow) => ({
                id: idGetter(item) as number,
                code: item.code,
            }));
            selectedItem = event.dataItems.find((x) => selectedState[idGetter(x)] === true) as SubjectHubGridRow;
        } else {
            selectedItem = event.startDataItem as SubjectHubGridRow;
        }

        setSelectionState((old) => ({
            ...old,
            selectedItemsCount: selectedRowCount,
            bulkUpdateRows: selectedItemsProps,
            selectedField: selectedItem?.code,
        }));
    };
    const [selectedSubjectForRemoval, setSelectedSubjectForRemoval] = useState<{
        id: number;
        code: string;
        reviewRecordId: number;
    } | null>(null);
    const showAddDialog = useBoolean(false);
    const isEditSubject = useBoolean(false);
    const showSubjectReviewDialog = useBoolean(false);
    const showRemoveFromReviewDialog = useBoolean(false);
    const showRemoveFromReviewBulkDialog = useBoolean(false);
    const reviewAll = useBoolean(false);
    const removeAll = useBoolean(false);
    const tagAll = useBoolean(false);
    const user = useAuthorization();
    const parentFilter = useRef<CompositeFilterDescriptor>(null);

    const canEdit = user?.canWrite?.({
        module: Constants.Modules.SM,
        permission: Permissions.ReadWrite,
        element: Constants.DataElements.SubjectMonitoring,
    });

    const smActiveReviewContext = useContext(SMActiveReviewContext);
    const inReviewMode = smActiveReviewContext?.state?.reviewRecordId != null;

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(
        studyRef,
        smActiveReviewContext?.state.reviewRecordId,
    );
    const review = getReviewQuery.data;

    const getRowMenuActions = (isOnReview?: boolean | null): RowMenuAction[] => {
        return [
            {
                id: 1,
                label: t('navigateIcon'),
                onClick: (dataItem) => {
                    if (!dataItem) {
                        return;
                    }
                    const subject = dataItem as IDataItem;

                    if (!subject) {
                        return;
                    }

                    setSessionStorageSubjectCode(subject.code);
                    // Need to also tell the navbar to programmatically select "Subject Profile"
                    localStateDispatch({
                        type: 'SET_SUBJECT_PROFILE_NAVIGATION',
                        setSubjectProfileNavigation: true,
                    });
                    // Cannot use AllRoutes dictionary due to circular dependency.
                    navigate({ pathname: '/subject-monitoring/subject-profile' });
                },
                icon: ['far', 'arrow-up-right'],
            },
            {
                id: 2,
                label: t('editTags'),
                onClick: () => {
                    isEditSubject.setTrue();
                    showAddDialog.setTrue();
                },
                icon: faRegularTagCirclePlus,
                rowDisabled: () => !canEdit,
            },
            {
                id: 3,
                label: t('addReview'),
                onClick: () => {
                    showSubjectReviewDialog.setTrue();
                },
                icon: faRegularMemoCirclePlus,
                rowDisabled: () => !canEdit || isOnReview === true,
            },
            {
                id: 4,
                label: t('removeReview'),
                onClick: (dataItem: unknown) => {
                    const subject = dataItem as SubjectHubGridRow;
                    const reviewRecordId = smActiveReviewContext?.state?.reviewRecordId;
                    if (!subject?.id || !reviewRecordId) return;
                    setSelectedSubjectForRemoval({
                        id: subject.id,
                        code: subject.code,
                        reviewRecordId,
                    });
                    showRemoveFromReviewDialog.setTrue();
                },
                icon: ['fas', 'trash-xmark'],
                colorProp: 'Red',
                rowDisabled: () => !canEdit || !isOnReview,
            },
        ];
    };

    const getApiGridMenuActionItems = (isOnReview?: boolean | null): GridMenuItemProps[] => {
        return [
            {
                title: `${t('grid.tagSelected')}`,
                'data-testid': 'add-tag',
                customIcon: (
                    <FontAwesomeIcon aria-label={`${t('tag')}`} icon={['far', 'tag']} style={{ fontSize: 18 }} />
                ),
                onClick: () => {
                    isEditSubject.setFalse();
                    showAddDialog.setTrue();
                },
                disabled: !selectionState.selectedField || !canEdit,
            },
            {
                title: `${t('grid.tagAll')}`,
                'data-testid': 'tag-All',
                customIcon: (
                    <FontAwesomeIcon aria-label={`${t('tagAll')}`} icon={['far', 'tags']} style={{ fontSize: 18 }} />
                ),
                onClick: () => {
                    isEditSubject.setFalse();
                    showAddDialog.setTrue();
                    tagAll.setTrue();
                },
                disabled:
                    !canEdit || (isOnReview && !onReviewGridHasRecords) || (!isOnReview && !notOnReviewGridHasRecords),
            },
            ...(isOnReview === true
                ? [
                      {
                          title: t('grid.removeSelectedFromReview'),
                          'data-testid': 'remove-selected-from-review',
                          customIcon: (
                              <FontAwesomeIcon
                                  aria-label={t('grid.removeSelectedFromReview')}
                                  icon={['far', 'trash-xmark']}
                                  style={{ fontSize: 18 }}
                              />
                          ),
                          onClick: () => {
                              showRemoveFromReviewBulkDialog.setTrue();
                          },
                          disabled: !selectionState.selectedField || !canEdit,
                      },
                      {
                          title: t('grid.removeAllFromReview'),
                          'data-testid': 'remove-all-from-review',
                          customIcon: (
                              <FontAwesomeIcon
                                  aria-label={t('grid.removeAllFromReview')}
                                  icon={['far', 'trash-list']}
                                  style={{ fontSize: 18 }}
                              />
                          ),
                          onClick: () => {
                              removeAll.setTrue();
                              showRemoveFromReviewBulkDialog.setTrue();
                          },
                          disabled: !canEdit || !onReviewGridHasRecords,
                      },
                  ]
                : [
                      {
                          title: `${t('grid.reviewSelected')}`,
                          'data-testid': 'add-to-review',
                          customIcon: (
                              <FontAwesomeIcon
                                  aria-label={`${t('addToReview')}`}
                                  icon={['far', 'file-plus']}
                                  style={{ fontSize: 18 }}
                              />
                          ),
                          onClick: () => {
                              showSubjectReviewDialog.setTrue();
                          },
                          disabled: !selectionState.selectedField || !canEdit,
                      },
                      {
                          title: `${t('grid.reviewAll')}`,
                          'data-testid': 'review-all',
                          customIcon: (
                              <FontAwesomeIcon
                                  aria-label={`${t('reviewAll')}`}
                                  icon={['far', 'files-medical']}
                                  style={{ fontSize: 18 }}
                              />
                          ),
                          onClick: () => {
                              showSubjectReviewDialog.setTrue();
                              reviewAll.setTrue();
                          },
                          disabled: !canEdit || !notOnReviewGridHasRecords,
                      },
                  ]),
        ];
    };

    const parentGridRef = useRef<ApiGridHandle>(null);
    const bottomGridRef = useRef<ApiGridHandle>(null);

    const onClose = () => {
        showAddDialog.setFalse();
        showSubjectReviewDialog.setFalse();
        reviewAll.setFalse();
        tagAll.setFalse();
    };

    /**
     * Refreshes the config and data for the subject overview grids
     */
    const refetchDataAndConfig = () => {
        parentGridRef?.current?.refetchConfig();
        parentGridRef?.current?.refreshData(false, true);
        bottomGridRef?.current?.refetchConfig();
        bottomGridRef?.current?.refreshData(false, true);
    };

    const onCloseRemoveFromReview = () => {
        showRemoveFromReviewDialog.setFalse();
        setSelectedSubjectForRemoval(null);
    };

    const onErrorRemoveFromReview = () => {
        ShowError(tCommon('common:errorMessage'));
        onCloseRemoveFromReview();
    };

    const onSuccessRemoveFromReview = () => {
        refetchDataAndConfig();
        onCloseRemoveFromReview();
        SubjectMonitoringService.refreshSubjectsInEditReviewPanel();
        ShowMessage({ text: tCommon('actionsSuccessfulToast'), type: 'success' });
    };

    const onCloseRemoveFromReviewBulk = () => {
        showRemoveFromReviewBulkDialog.setFalse();
        removeAll.setFalse();
    };

    const onErrorRemoveFromReviewBulk = () => {
        ShowError(tCommon('common:errorMessage'));
        onCloseRemoveFromReviewBulk();
    };

    const onSuccessRemoveFromReviewBulk = () => {
        refetchDataAndConfig();
        onCloseRemoveFromReviewBulk();
        SubjectMonitoringService.refreshSubjectsInEditReviewPanel();
        ShowMessage({ text: tCommon('actionsSuccessfulToast'), type: 'success' });
    };

    const onSubmit = () => {
        SubjectMonitoringService.refreshManageTagGrid(true);
        SubjectMonitoringService.refreshSubjectsInEditReviewPanel();
        refetchDataAndConfig();
        showAddDialog.setFalse();
        isEditSubject.setFalse();
        reviewAll.setFalse();
        tagAll.setFalse();
    };

    const studyDashboardProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setComparisonDataset,
        }),
        [formData, setFormData, comparisonDataset],
    );

    useEffect(() => {
        setFormData({
            ...formData,
            studyRef,
            dataset: {
                id: dataset?.id as number,
                date: new Date(dataset?.dataCutDate as Date | string | number),
            },
            datasetFilters: {
                ...formData.datasetFilters,
                comparisonDataset,
            },
        });
    }, [studyRef, dataset, comparisonDataset]);

    useEffect(() => {
        let route = location.pathname;
        const queryParams = new URLSearchParams(location.search);

        if (sessionId) queryParams.append('copySessionId', sessionId);
        if (queryParams.size) route = `${location.pathname}?${queryParams.toString()}`;

        pageToolbarDispatch({ type: 'setPopoutRoute', popoutRoute: route });
    }, [sessionId]);

    useEffect(() => {
        return () => {
            SubjectMonitoringService.refreshTagGrid(false);
            SubjectMonitoringService.refreshManageTagGrid(false);
        };
    }, []);

    useEffect(() => {
        const subscribeDefault = SubjectMonitoringService.onRefreshSubjectHubGrid().subscribe((refreshGrid) => {
            if (refreshGrid) {
                refetchDataAndConfig();
            }
        });

        return () => subscribeDefault.unsubscribe();
    }, []);

    const getGridTitle = (subjectsOnReview?: boolean | null) => {
        if (subjectsOnReview === null || subjectsOnReview === undefined) {
            return t('grid.title');
        }
        if (subjectsOnReview) {
            return t('grid.titleOnReview');
        }
        return t('grid.titleNotOnReview');
    };

    const getFilenameForExport = (subjectsOnReview?: boolean | null) => {
        return `${study?.studyNumber} ${getGridTitle(subjectsOnReview)} ${moment(dataset.dataCutDate).format('DD-MMM-yyyy')}`;
    };

    const renderSubjectGrid = (
        ref: React.RefObject<ApiGridHandle | null>,
        testDataId: string,
        split: boolean,
        subjectIsOnAReview?: boolean | null,
        subjectsOnReviewId?: number | null,
    ) => (
        <ApiGrid
            ref={ref}
            dataItemKey="id"
            title={getGridTitle(subjectIsOnAReview)}
            testDataId={testDataId}
            maxHeight={split ? 'calc(50vh)' : 'calc(100vh)'}
            table={DEFAULT_GRID_TABLE}
            gridParams={{
                grid: 'DataTable',
                studyRef,
                dataset: dataset.id,
                comparisonDatasetId: comparisonDataset?.id,
                subjectsOnReviewId,
                subjectIsOnAReview,
            }}
            showAddIcon={false}
            showPlaceholder={false}
            exportFilename={getFilenameForExport(subjectIsOnAReview)}
            screenFilterable
            filterable
            noPadding
            rowMenuActions={getRowMenuActions(subjectIsOnAReview)}
            apiGridMenuActionItems={getApiGridMenuActionItems(subjectIsOnAReview)}
            onSelectionChange={onSelectionChange}
            selectable={{ enabled: true, mode: 'multiple', drag: true }}
            filterListener={(newFilterState) => {
                parentFilter.current = newFilterState || null;
            }}
            scrollable="virtual"
            onTotalRowsCountChanged={(count) => {
                if (subjectIsOnAReview) {
                    setOnReviewGridHasRecords(count > 0);
                } else {
                    setNotOnReviewGridHasRecords(count > 0);
                }
            }}
        />
    );

    const heightOffset = isPopoutPage ? 141 : 178;

    return (
        <StudySiteHubContext.Provider value={studyDashboardProviderValue}>
            <div style={{ height: `calc(100vh - ${heightOffset}px - var(--page-banners-height)` }}>
                <ActionBar
                    studyRef={studyRef}
                    datasetId={dataset.id}
                    filterActions={FilterUIConfigType.SubjectMonitoringHub}
                    className="k-mb-3"
                    datasetDropdownSelector
                />
                {sessionId && dataset.id && studyRef && (
                    <>
                        {showAddDialog.value && (
                            <AddEditSubjectTagsDialog
                                studyRef={studyRef}
                                subjectCodes={selectionState.bulkUpdateRows.map((x) => x.code)}
                                onClose={onClose}
                                onSubmit={onSubmit}
                                datasetId={dataset.id}
                                isEditSubject={isEditSubject.value}
                                tagAll={tagAll.value}
                                parentFilter={parentFilter}
                                comparisonDataset={comparisonDataset?.id}
                                totalRows={parentGridRef?.current?.getTotalRows() ?? 0}
                            />
                        )}
                        {showRemoveFromReviewDialog.value &&
                            selectedSubjectForRemoval &&
                            smActiveReviewContext?.state?.sessionId && (
                                <RemoveFromReviewDialog
                                    datasetId={dataset.id}
                                    sessionId={smActiveReviewContext.state.sessionId}
                                    studyRef={studyRef}
                                    subjectId={selectedSubjectForRemoval.id}
                                    reviewRecordId={selectedSubjectForRemoval.reviewRecordId}
                                    onClose={onCloseRemoveFromReview}
                                    onSuccess={onSuccessRemoveFromReview}
                                    onError={onErrorRemoveFromReview}
                                />
                            )}
                        {showRemoveFromReviewBulkDialog.value &&
                            smActiveReviewContext?.state?.reviewRecordId &&
                            smActiveReviewContext?.state?.sessionId && (
                                <RemoveFromReviewBulkDialog
                                    datasetId={dataset.id}
                                    studyRef={studyRef}
                                    subjects={selectionState.bulkUpdateRows}
                                    removeAll={removeAll.value}
                                    reviewRecordId={smActiveReviewContext.state.reviewRecordId}
                                    sessionId={smActiveReviewContext.state.sessionId}
                                    totalRows={parentGridRef?.current?.getTotalRows() ?? 0}
                                    onClose={onCloseRemoveFromReviewBulk}
                                    onSuccess={onSuccessRemoveFromReviewBulk}
                                    onError={onErrorRemoveFromReviewBulk}
                                />
                            )}
                        {showSubjectReviewDialog.value && (
                            <SubjectReviewDialog
                                studyRef={studyRef}
                                onClose={onClose}
                                subjects={Object.fromEntries(
                                    selectionState.bulkUpdateRows.map((x) => [x.id as unknown as string, x.code]),
                                )}
                                reviewAll={reviewAll.value}
                                parentFilter={parentFilter}
                                comparisonDataset={comparisonDataset?.id}
                                totalRows={
                                    (inReviewMode
                                        ? bottomGridRef?.current?.getTotalRows()
                                        : parentGridRef?.current?.getTotalRows()) ?? 0
                                }
                                onSubmit={() => {
                                    refetchDataAndConfig();
                                }}
                                preselectedReview={review}
                            />
                        )}
                        {inReviewMode && review?.id ? (
                            <CoreSplitter
                                className="!k-h-full"
                                orientation={SplitterOrientation.vertical}
                                data-testid="subject-hub-review-mode-splitter"
                            >
                                <div className="k-h-full">
                                    <div className={cn(style.splitterSectionHeight, 'k-h-full')}>
                                        {renderSubjectGrid(parentGridRef, 'top-grid', inReviewMode, true, review.id)}
                                    </div>
                                </div>
                                <div className="k-h-full">
                                    <div className={cn(style.splitterSectionHeight, 'k-h-full')}>
                                        {renderSubjectGrid(
                                            bottomGridRef,
                                            'bottom-grid',
                                            inReviewMode,
                                            false,
                                            review.id,
                                        )}
                                    </div>
                                </div>
                            </CoreSplitter>
                        ) : (
                            renderSubjectGrid(parentGridRef, 'subjects-grid', inReviewMode)
                        )}
                    </>
                )}
            </div>
        </StudySiteHubContext.Provider>
    );
};

const Wrapper = ({ study, cm2Dataset: dataset }: LoggedInPageProps) => {
    const params = new URLSearchParams(window.location.search);
    const copySessionId: string | undefined = params.get('copySessionId') ?? undefined;
    const studyRef = study?.id as string;

    return (
        <StudyDataFilterContextProvider
            studyRef={studyRef}
            datasetId={dataset?.id as number}
            key={dataset?.id as number}
            preAppliedFilters={{
                datasetId: dataset?.id as number,
                filters: [],
            }}
            copySessionId={copySessionId}
            recalculateOnDatasetChange
        >
            <Content
                dataset={{
                    id: dataset?.id as number,
                    dataCutDate: new Date(dataset?.dataCutDate as Date | string | number),
                    created: new Date(dataset?.created as Date | string | number),
                }}
                studyRef={studyRef}
            />
        </StudyDataFilterContextProvider>
    );
};

export default Wrapper;
